import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Button, Card, CardBody, CardHeader, Spinner } from "@heroui/react";
import { parseAbsoluteToLocal, parseDateTime } from "@internationalized/date";
import { useNavigate, useParams } from "react-router-dom";
import { useFetchData, usePostData } from "@/hooks";
import { showToast, clearToast } from "@/utils";
import { PageHeader, ThemedText, ErrorState, Stepper } from "@/components/ui";
import {
    LuArrowLeft,
    LuArrowRight,
    LuSave,
    LuFileText,
    LuImage,
} from "react-icons/lu";
import { TransferInfoStep, MediaStep } from "./components";

const CashTransferCreate = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditing = !!id;

    const steps = [
        { id: 1, title: "Rincian Transfer", icon: LuFileText },
        { id: 2, title: "Bukti Transfer", icon: LuImage },
    ];

    const [currentStep, setCurrentStep] = useState(1);

    const {
        control,
        handleSubmit,
        watch,
        setValue,
        reset,
        trigger,
        formState: { errors },
    } = useForm({
        defaultValues: {
            transaction_date: parseAbsoluteToLocal(new Date().toISOString()),
            source_master_cash_id: "",
            destination_master_cash_id: "",
            amount: "",
            description: "",
            transaction_proof_photo: [],
        },
    });

    // ------------------- Data Fetching -------------------

    // 1. Initial Data (Edit Mode)
    const {
        data: initialData,
        isLoading: isLoadingInitial,
        error: errorInitial,
        refetch,
    } = useFetchData(isEditing ? `cash-transfer/${id}/edit` : null);

    // 2. Master Cashes
    const { data: masterCashes, isLoading: isLoadingMasterCashes } =
        useFetchData("select/master-cash");

    // ------------------- Mutations -------------------
    const mutationEndpoint = isEditing
        ? `cash-transfer/${id}/update`
        : "cash-transfer/store";
    const { mutate: mutateData, isLoading: isMutating } =
        usePostData(mutationEndpoint);

    // ------------------- Effects -------------------

    useEffect(() => {
        if (isEditing && initialData) {
            reset({
                transaction_date: parseDateTime(
                    initialData.transaction_date.replace(" ", "T"),
                ),
                description: initialData.description || "",
                amount: initialData.amount,
                source_master_cash_id: String(
                    initialData.source_master_cash_id,
                ),
                destination_master_cash_id: String(
                    initialData.destination_master_cash_id,
                ),
                transaction_proof_photo: initialData.transaction_proof_image
                    ? [
                          {
                              isExisting: true,
                              preview:
                                  initialData.transaction_proof_image.url_600 ||
                                  initialData.transaction_proof_image.url,
                              id: initialData.transaction_proof_image.id,
                              file: null, // Important for logic check
                          },
                      ]
                    : [],
            });
        }
    }, [isEditing, initialData, reset]);

    // ------------------- Validation & Navigation -------------------

    const validateStep = async (step) => {
        let fields = [];
        switch (step) {
            case 1:
                fields = [
                    "transaction_date",
                    "source_master_cash_id",
                    "destination_master_cash_id",
                    "amount",
                ];
                break;
            default:
                break;
        }
        return await trigger(fields);
    };

    const handleNext = async () => {
        const isValid = await validateStep(currentStep);
        if (isValid) {
            clearToast();
            setCurrentStep((prev) => Math.min(prev + 1, steps.length));
        } else {
            showToast(
                "error",
                "Validasi Gagal",
                "Mohon lengkapi data yang wajib diisi.",
            );
        }
    };

    const handlePrev = () => {
        setCurrentStep((prev) => Math.max(prev - 1, 1));
    };

    // ------------------- Submit -------------------

    const onSubmit = (data) => {
        const formData = new FormData();
        const date = data.transaction_date.toDate();
        const formattedDate =
            date.getFullYear() +
            "-" +
            String(date.getMonth() + 1).padStart(2, "0") +
            "-" +
            String(date.getDate()).padStart(2, "0") +
            " " +
            String(date.getHours()).padStart(2, "0") +
            ":" +
            String(date.getMinutes()).padStart(2, "0") +
            ":" +
            String(date.getSeconds()).padStart(2, "0");

        formData.append("transaction_date", formattedDate);
        if (data.description) formData.append("description", data.description);
        formData.append("amount", data.amount);
        formData.append("source_master_cash_id", data.source_master_cash_id);
        formData.append(
            "destination_master_cash_id",
            data.destination_master_cash_id,
        );

        // Image Handling Logic
        if (
            data.transaction_proof_photo &&
            data.transaction_proof_photo.length > 0
        ) {
            const img = data.transaction_proof_photo[0];
            // Only append if it's a new file (not existing)
            if (!img.isExisting && img.file) {
                formData.append("transaction_proof_photo", img.file);
            }
        }

        if (isEditing) {
            formData.append("_method", "PUT");
        }

        mutateData(formData, {
            headers: { "Content-Type": "multipart/form-data" },
            onSuccess: () => {
                showToast(
                    "success",
                    "Berhasil",
                    `Transfer kas berhasil ${isEditing ? "diperbarui" : "dicatat"}!`,
                );
                navigate("/cash-transfer");
            },
            onError: (err) => {
                showToast(
                    "error",
                    "Gagal",
                    err.response?.data?.message ||
                        "Terjadi kesalahan saat menyimpan data",
                );
            },
        });
    };

    if (isEditing && errorInitial) {
        return (
            <ErrorState
                title="Gagal Memuat Data"
                description="Terjadi kesalahan saat memuat data transfer."
                onAction={refetch}
            />
        );
    }

    return (
        <div className="mx-auto flex max-w-3xl flex-col gap-6">
            <div className="flex gap-4">
                <Button isIconOnly variant="light" onPress={() => navigate(-1)}>
                    <LuArrowLeft size={20} />
                </Button>
                <PageHeader
                    title={
                        isEditing ? "Edit Transfer Kas" : "Buat Transfer Kas"
                    }
                    breadcrumbs={[
                        { label: "Dashboard", href: "/" },
                        { label: "Transfer Kas", href: "/cash-transfer" },
                        {
                            label: isEditing
                                ? "Edit Transfer"
                                : "Buat Transfer",
                        },
                    ]}
                />
            </div>

            <Card className="border-default-200 border" shadow="none">
                <CardBody className="p-6">
                    <Stepper steps={steps} currentStep={currentStep} />
                </CardBody>
            </Card>

            <form onSubmit={(e) => e.preventDefault()}>
                <Card className="border-default-200 border" shadow="none">
                    <CardHeader className="bg-default-100/70 px-6 py-4">
                        <ThemedText className="font-semibold">
                            {steps[currentStep - 1].title}
                        </ThemedText>
                    </CardHeader>
                    <CardBody className="p-6">
                        {isEditing && isLoadingInitial ? (
                            <div className="flex h-48 items-center justify-center">
                                <Spinner label="Memuat Data..." />
                            </div>
                        ) : (
                            <>
                                {currentStep === 1 && (
                                    <TransferInfoStep
                                        control={control}
                                        errors={errors}
                                        masterCashes={masterCashes || []}
                                        watch={watch}
                                    />
                                )}

                                {currentStep === 2 && (
                                    <MediaStep
                                        control={control}
                                        errors={errors}
                                    />
                                )}
                            </>
                        )}
                    </CardBody>
                </Card>

                <div className="mt-6 flex justify-between">
                    <Button
                        type="button"
                        variant="flat"
                        size="sm"
                        onPress={
                            currentStep === 1
                                ? () => navigate("/cash-transfer")
                                : handlePrev
                        }
                        startContent={<LuArrowLeft />}
                    >
                        {currentStep === 1 ? "Batal" : "Sebelumnya"}
                    </Button>

                    {currentStep < steps.length ? (
                        <Button
                            type="button"
                            color="primary"
                            size="sm"
                            onPress={handleNext}
                            endContent={<LuArrowRight />}
                        >
                            Selanjutnya
                        </Button>
                    ) : (
                        <Button
                            color="primary"
                            size="sm"
                            isLoading={isMutating}
                            onPress={handleSubmit(onSubmit)}
                            startContent={!isMutating && <LuSave />}
                        >
                            {isEditing ? "Simpan Perubahan" : "Simpan Data"}
                        </Button>
                    )}
                </div>
            </form>
        </div>
    );
};

export default CashTransferCreate;
